/**
 * The browser's own speech recognition (SA-1310 spec §8), for when there is no Deepgram key:
 * continuous, with interim words, stopping after `silenceMs` with no new words. The final words reach
 * `onFinal` once, when listening ends. A refused microphone reads `denied`.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

interface RecognitionResult {
  isFinal: boolean
  0: { transcript: string }
}
interface RecognitionEvent {
  results: ArrayLike<RecognitionResult>
}
interface Recognition {
  continuous: boolean
  interimResults: boolean
  lang: string
  onresult: ((e: RecognitionEvent) => void) | null
  onerror: ((e: { error: string }) => void) | null
  onend: (() => void) | null
  start(): void
  stop(): void
  abort(): void
}
type RecognitionCtor = new () => Recognition

function recognitionCtor(): RecognitionCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

/** A Deepgram language code as a tag the browser takes; `multi` is the browser's own language. */
export function browserLang(lang: string): string {
  return lang && lang !== 'multi' ? lang : (typeof navigator !== 'undefined' && navigator.language) || 'en'
}

export interface UseBrowserSpeechOptions {
  lang: string
  onFinal: (text: string) => void
  silenceMs?: number
}

export function useBrowserSpeech({ lang, onFinal, silenceMs = 2000 }: UseBrowserSpeechOptions) {
  const [supported] = useState(() => recognitionCtor() !== null)
  const [listening, setListening] = useState(false)
  const [partial, setPartial] = useState('')
  const [denied, setDenied] = useState(false)
  const recRef = useRef<Recognition | null>(null)
  const textRef = useRef('')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onFinalRef = useRef(onFinal)
  onFinalRef.current = onFinal

  const clearTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = null
  }

  const stop = useCallback(() => {
    clearTimer()
    recRef.current?.stop()
  }, [])

  const start = useCallback(() => {
    const Ctor = recognitionCtor()
    if (!Ctor || recRef.current) return
    const rec = new Ctor()
    rec.continuous = true
    rec.interimResults = true
    rec.lang = browserLang(lang)
    textRef.current = ''
    let finished = false
    const armSilence = () => {
      clearTimer()
      timerRef.current = setTimeout(() => rec.stop(), silenceMs)
    }
    rec.onresult = (e) => {
      let finals = ''
      let interim = ''
      for (let i = 0; i < e.results.length; i++) {
        const r = e.results[i]
        if (r.isFinal) finals += r[0].transcript
        else interim += r[0].transcript
      }
      textRef.current = (finals + interim).trim()
      setPartial(textRef.current)
      armSilence()
    }
    rec.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') setDenied(true)
    }
    rec.onend = () => {
      if (finished) return
      finished = true
      clearTimer()
      recRef.current = null
      setListening(false)
      setPartial('')
      const text = textRef.current.trim()
      textRef.current = ''
      if (text) onFinalRef.current(text)
    }
    recRef.current = rec
    setPartial('')
    setListening(true)
    rec.start()
    armSilence()
  }, [lang, silenceMs])

  useEffect(() => () => {
    clearTimer()
    const rec = recRef.current
    if (rec) {
      rec.onend = null
      rec.abort()
    }
  }, [])

  return { supported, listening, partial, denied, start, stop }
}
